import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Table,
  Typography,
  Tag,
  Spin,
  Collapse,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useReceiptVoucher, usePostReceiptVoucher, useCancelReceiptVoucher } from '@/hooks/useReceiptVoucher';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { ReceiptVoucher, ReceiptVoucherAllocation } from '@/services/receiptVoucher.service';
import { parseReceiptSettlementSnapshot } from '@/services/receiptVoucher.service';
import type { ColumnsType } from 'antd/es/table';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_RECEIPT_VOUCHER } from '@/constants/document-entity-types';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;

const ReceiptVoucherDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: receipt, isLoading } = useReceiptVoucher(id);
  const postMutation = usePostReceiptVoucher();
  const cancelMutation = useCancelReceiptVoucher();

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: tc('draft'),
      Posted: tc('posted'),
      Cancelled: tc('cancelled'),
    };
    return map[status] || status;
  };

  const receiptTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      AgainstInvoice: t('receiptTypeAgainstInvoice'),
      DirectRevenue: t('receiptTypeDirectRevenue'),
      AdvanceFromCustomer: t('receiptTypeAdvanceFromCustomer'),
      OnAccount: t('receiptTypeOnAccount'),
      Other: t('receiptTypeOther'),
    };
    return map[type] || type;
  };

  const paymentModeLabel = (mode: string) => {
    const map: Record<string, string> = {
      Cash: t('modeCash'),
      Cheque: t('modeCheque'),
      BankTransfer: t('modeBankTransfer'),
      Card: t('modeCard'),
      Online: t('modeOnline'),
      Other: t('modeOther'),
    };
    return map[mode] || mode;
  };

  const breadcrumbSegments = useMemo(
    () =>
      receipt
        ? [
            { title: t('financeModule'), path: '/fin/dashboard' },
            { title: t('receiptVouchers'), path: '/fin/receipt-vouchers' },
            { title: receipt.receipt_number || t('receipt') },
          ]
        : null,
    [receipt?.id, receipt?.receipt_number, t]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const rvWorkflowStages = useMemo(() => {
    if (!receipt) return [];
    const st = receipt.status;
    const cancelled = st === 'Cancelled';
    const posted = st === 'Posted';
    return [
      {
        title: tc('draft'),
        description: t('workflowEntry'),
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: tc('posted'),
        description: t('workflowGl'),
        status: (cancelled ? 'wait' : posted ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [receipt, t, tc]);

  const rvWorkflowCurrent = useMemo(() => {
    if (!receipt || receipt.status === 'Cancelled') return 0;
    if (receipt.status === 'Draft') return 0;
    if (receipt.status === 'Posted') return 1;
    return 0;
  }, [receipt]);

  if (!id) {
    navigate('/fin/receipt-vouchers');
    return null;
  }
  if (isLoading && !receipt) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!receipt) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="secondary">{t('receiptVoucherNotFound')}</Text>
        </Card>
      </div>
    );
  }

  const isDraft = receipt.status === 'Draft';
  const isPosted = receipt.status === 'Posted';

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/receipt-vouchers')}>
        {tc('back')}
      </Button>
      {isDraft && (
        <>
          <Button icon={<EditOutlined />} onClick={() => navigate(`/fin/receipt-vouchers/${id}/edit`)}>
            {tc('edit')}
          </Button>
          <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => postMutation.mutate(id!)} loading={postMutation.isPending}>
            {tc('post')}
          </Button>
        </>
      )}
      <ReportDocumentActions
        reportKey="receipt-voucher"
        params={id ? { id } : null}
        documentId={id}
        sendPermissions={['finance.voucher.create']}
        emailLabel={t('email')}
      />
      {isPosted && (
        <>
          <ReportDocumentActions
            reportKey="accounting-voucher"
            params={id ? { source_type: 'receipt_voucher', source_id: id } : null}
            documentId={id}
            sendPermissions={['finance.voucher.create']}
            emailLabel={t('emailGl')}
          />
          <Button danger icon={<StopOutlined />} onClick={() => cancelMutation.mutate(id!)} loading={cancelMutation.isPending}>
            {tc('cancel')}
          </Button>
        </>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>{t('quickLinks')}</Title>
      <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/fin/receipt-vouchers')}>
        {t('allReceiptVouchers')}
      </Button>
    </div>
  );

  const collapseItems = [
    {
      key: 'header',
      label: t('receiptDetailsSection'),
      children: (
        <div>
          <Card bordered={false} styles={{ body: { padding: 0 } }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label={t('receiptDate')}>{receipt.receipt_date ? dayjs(receipt.receipt_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
              <Descriptions.Item label={t('department')}>
                {receipt.department_id
                  ? (() => {
                      const d = departments.find((x) => x.id === receipt.department_id);
                      return d ? `${d.department_code} - ${d.department_name}` : receipt.department_id;
                    })()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('receiptType')}>{receiptTypeLabel(receipt.receipt_type)}</Descriptions.Item>
              <Descriptions.Item label={t('customer')}>{(receipt as ReceiptVoucher & { customer?: { customer_name?: string } }).customer?.customer_name ?? '-'}</Descriptions.Item>
              <Descriptions.Item label={t('cashAmount')}>{formatCurrency(Number(receipt.amount))}</Descriptions.Item>
              <Descriptions.Item label={t('currency')}>{receipt.currency ?? 'AED'}</Descriptions.Item>
              <Descriptions.Item label={t('paymentMode')}>{paymentModeLabel(receipt.payment_mode)}</Descriptions.Item>
              <Descriptions.Item label={t('receivingAccount')}>
                {(receipt as ReceiptVoucher & { receivingAccount?: { account_code: string; account_name: string } }).receivingAccount
                  ? `${(receipt as any).receivingAccount.account_code} - ${(receipt as any).receivingAccount.account_name}`
                  : '-'}
              </Descriptions.Item>
              {(receipt.chargeLines?.length ?? 0) > 0 && (
                <>
                  <Descriptions.Item label={t('additionalCharges')}>
                    {formatCurrency(
                      (receipt.chargeLines ?? []).reduce((s, l) => s + Number(l.amount), 0)
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('netBankInflow')}>
                    {formatCurrency(
                      Number(receipt.amount) -
                        (receipt.chargeLines ?? []).reduce((s, l) => s + Number(l.amount), 0)
                    )}
                  </Descriptions.Item>
                </>
              )}
              {receipt.receipt_type === 'AgainstInvoice' && Number(receipt.write_off_amount ?? 0) > 0 && (
                <>
                  <Descriptions.Item label={t('writeOff')}>
                    {formatCurrency(Number(receipt.write_off_amount ?? 0))}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('writeOffAccount')}>
                    {receipt.writeOffAccount
                      ? `${receipt.writeOffAccount.account_code} - ${receipt.writeOffAccount.account_name}`
                      : receipt.write_off_account_id ?? '—'}
                  </Descriptions.Item>
                </>
              )}
              {receipt.settlement_remarks && (
                <Descriptions.Item label={t('settlementRemarks')} span={2}>
                  {receipt.settlement_remarks}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('reference')}>{receipt.reference_number ?? '-'}</Descriptions.Item>
              <Descriptions.Item label={t('narration')} span={2}>{receipt.narration ?? '-'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </div>
      ),
    },
  ];
  if (
    (receipt.receipt_type === 'AgainstInvoice' || receipt.receipt_type === 'AdvanceFromCustomer') &&
    receipt.allocations?.length
  ) {
    const showAdvanceCreditCol = receipt.receipt_type === 'AdvanceFromCustomer';
    const allocationColumns: ColumnsType<ReceiptVoucherAllocation> = [
      {
        title: t('invoice'),
        key: 'invoice',
        render: (_, row) => (
          <Space>
            <span>{row.salesInvoice?.invoice_number ?? '—'}</span>
            {row.salesInvoice?.invoice_type === 'Advance' && <Tag color="blue">{t('advanceInvoice')}</Tag>}
          </Space>
        ),
      },
    ];
    if (showAdvanceCreditCol) {
      allocationColumns.push({
        title: t('creditAccountGl'),
        key: 'credit_gl',
        render: (_, row) =>
          row.creditAccount
            ? `${row.creditAccount.account_code} - ${row.creditAccount.account_name}`
            : t('fromInvoiceAtPost'),
      });
    }
    allocationColumns.push({
      title: t('allocatedAmount'),
      dataIndex: 'allocated_amount',
      key: 'allocated_amount',
      align: 'right',
      render: (v: number) => formatCurrency(Number(v)),
    });
    collapseItems.push({
      key: 'alloc',
      label: receipt.receipt_type === 'AdvanceFromCustomer' ? t('allocationsAdvanceInvoices') : t('allocationsToInvoices'),
      children: (
        <div>
          <Table
            size="small"
            rowKey="sales_invoice_id"
            dataSource={receipt.allocations}
            pagination={false}
            columns={allocationColumns}
          />
        </div>
      ),
    });
  }
  if (receipt.directLines?.length) {
    collapseItems.push({
      key: 'direct',
      label: t('directLines'),
      children: (
        <div>
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={receipt.directLines}
            pagination={false}
            columns={[
              {
                title: t('account'),
                key: 'account',
                render: (_: unknown, row: { account?: { account_code: string; account_name: string }; account_id?: string }) =>
                  row.account ? `${row.account.account_code} - ${row.account.account_name}` : '—',
              },
              { title: t('amount'), dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (v: number) => formatCurrency(Number(v)) },
              { title: t('description'), dataIndex: 'description', key: 'description' },
            ]}
          />
        </div>
      ),
    });
  }
  if (receipt.receipt_type === 'AgainstInvoice' && receipt.discountLines?.some((l) => Number(l.amount) > 0)) {
    const discTotal = receipt.discountLines.reduce((s, l) => s + Number(l.amount || 0), 0);
    const writeOff = Number(receipt.write_off_amount ?? 0);
    collapseItems.push({
      key: 'discount',
      label: t('additionalDiscountFacility'),
      children: (
        <div>
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={receipt.discountLines.filter((l) => Number(l.amount) > 0)}
            pagination={false}
            columns={[
              {
                title: t('account'),
                key: 'account',
                render: (_: unknown, row: { account?: { account_code: string; account_name: string } }) =>
                  row.account ? `${row.account.account_code} - ${row.account.account_name}` : '—',
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                key: 'amount',
                align: 'right' as const,
                render: (v: number) => formatCurrency(Number(v)),
              },
              { title: t('description'), dataIndex: 'description', key: 'description' },
            ]}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {t('cashPlusDiscountsWriteOffEquals', {
              cash: formatCurrency(Number(receipt.amount)),
              discounts: formatCurrency(discTotal),
              writeOffPart: writeOff > 0 ? t('writeOffPart', { writeOff: formatCurrency(writeOff) }) : '',
              total: formatCurrency(Number(receipt.amount) + discTotal + writeOff),
            })}
          </Text>
        </div>
      ),
    });
  }

  const snapshotLines = parseReceiptSettlementSnapshot(receipt.settlement_snapshot);
  if (receipt.receipt_type === 'AgainstInvoice' && snapshotLines.length) {
    const snap =
      receipt.settlement_snapshot &&
      !Array.isArray(receipt.settlement_snapshot) &&
      typeof receipt.settlement_snapshot === 'object'
        ? receipt.settlement_snapshot
        : null;
    collapseItems.push({
      key: 'settlement',
      label: t('allocationSettlementDetails'),
      children: (
        <div>
          {snap && (
            <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label={t('cashReceived')}>
                {formatCurrency(Number(snap.cash_amount ?? receipt.amount))}
              </Descriptions.Item>
              <Descriptions.Item label={t('facilityDiscounts')}>
                {formatCurrency(Number(snap.discount_total ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label={t('writeOff')}>
                {formatCurrency(Number(snap.write_off_amount ?? receipt.write_off_amount ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label={t('cashAllocations')}>
                {formatCurrency(Number(snap.cash_allocation_total ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label={t('advances')}>
                {formatCurrency(Number(snap.advance_total ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label={t('creditNotes')}>
                {formatCurrency(Number(snap.credit_note_total ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label={t('invoiceCoverage')}>
                {formatCurrency(Number(snap.invoice_coverage_total ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label={t('charges')}>
                {formatCurrency(Number(snap.charge_total ?? 0))}
              </Descriptions.Item>
            </Descriptions>
          )}
          <Table
            size="small"
            rowKey="sales_invoice_id"
            dataSource={snapshotLines}
            pagination={false}
            columns={[
              {
                title: t('invoice'),
                dataIndex: 'sales_invoice_id',
                key: 'invoice',
                render: (invId: string) => {
                  const alloc = receipt.allocations?.find((a) => a.sales_invoice_id === invId);
                  return alloc?.salesInvoice?.invoice_number ?? invId;
                },
              },
              {
                title: t('cash'),
                dataIndex: 'cash_amount',
                align: 'right' as const,
                render: (v: number) => formatCurrency(Number(v ?? 0)),
              },
              {
                title: t('advances'),
                key: 'adv',
                align: 'right' as const,
                render: (_: unknown, row: (typeof snapshotLines)[0]) =>
                  formatCurrency(
                    (row.advance_adjustments ?? []).reduce((s, a) => s + Number(a.amount ?? 0), 0)
                  ),
              },
              {
                title: t('creditNotes'),
                key: 'cn',
                align: 'right' as const,
                render: (_: unknown, row: (typeof snapshotLines)[0]) =>
                  formatCurrency(
                    (row.credit_note_adjustments ?? []).reduce((s, a) => s + Number(a.amount ?? 0), 0)
                  ),
              },
              {
                title: t('lineTotal'),
                key: 'total',
                align: 'right' as const,
                render: (_: unknown, row: (typeof snapshotLines)[0]) => {
                  const cash = Number(row.cash_amount ?? 0);
                  const adv = (row.advance_adjustments ?? []).reduce((s, a) => s + Number(a.amount ?? 0), 0);
                  const cn = (row.credit_note_adjustments ?? []).reduce((s, a) => s + Number(a.amount ?? 0), 0);
                  return formatCurrency(cash + adv + cn);
                },
              },
            ]}
          />
        </div>
      ),
    });
  }

  if ((receipt.chargeLines?.length ?? 0) > 0) {
    collapseItems.push({
      key: 'charges',
      label: t('additionalCharges'),
      children: (
        <div>
          <Table
            size="small"
            rowKey={(_, i) => `charge-${i}`}
            dataSource={receipt.chargeLines}
            pagination={false}
            columns={[
              {
                title: t('account'),
                key: 'account',
                render: (_: unknown, row: { account?: { account_code: string; account_name: string } }) =>
                  row.account ? `${row.account.account_code} - ${row.account.account_name}` : '—',
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                key: 'amount',
                align: 'right' as const,
                render: (v: number) => formatCurrency(Number(v)),
              },
              { title: t('description'), dataIndex: 'description', key: 'description' },
            ]}
          />
        </div>
      ),
    });
  }

  return (
    <div style={{ margin: '-20px -20px 0' }}>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel={t('receiptVoucher')}
            documentNumber={receipt.receipt_number}
            statusTag={<Tag color={isPosted ? 'green' : isDraft ? 'default' : 'red'}>{statusLabel(receipt.status)}</Tag>}
            subtitle={
              <Text type="secondary">{receipt.receipt_date ? dayjs(receipt.receipt_date).format('DD MMM YYYY') : ''}</Text>
            }
            facts={
              <Space wrap>
                <Text>
                  <strong>{t('amount')}:</strong> {formatCurrency(Number(receipt.amount))}
                </Text>
                <Text>
                  <strong>{t('type')}:</strong> {receiptTypeLabel(receipt.receipt_type)}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={rvWorkflowStages} current={rvWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <Collapse defaultActiveKey={collapseItems.map((i) => i.key)} items={collapseItems} />
        <div style={{ marginTop: 16 }}>
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_RECEIPT_VOUCHER}
            entityId={receipt.id}
            title={tc('attachments')}
            readOnly={receipt.status === 'Cancelled'}
            queryKeyPrefix={['documents', 'receipt-voucher']}
            uploadHint={t('attachmentsUploadHint')}
          />
          <InheritedAttachmentsCard
            trailDomain="sales"
            entityType={DOCUMENT_ENTITY_TYPE_RECEIPT_VOUCHER}
            entityId={receipt.id}
            queryKeyPrefix={['documents', 'receipt-voucher']}
          />
        </div>
      </WorkspaceLayout>
    </div>
  );
};

export default ReceiptVoucherDetailPage;
